import React from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { cardSx } from "./platformUi";

const recentGrid =
  "minmax(0, 2fr) minmax(112px, 0.7fr) minmax(112px, 0.7fr)";

function StatCellSkeleton({ showDivider }) {
  return (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        px: "16px",
        py: "16px",
        boxSizing: "border-box",
        borderRight: showDivider ? "1px solid #E5E9EC" : "none",
      }}
    >
      <Skeleton variant="text" width="55%" height={16} sx={{ mb: "8px", borderRadius: "4px" }} />
      <Skeleton variant="text" width="40%" height={28} sx={{ borderRadius: "4px" }} />
    </Box>
  );
}

function StatsRowSkeleton({ count }) {
  return (
    <Box
      sx={{
        ...cardSx,
        display: "flex",
        flexDirection: "row",
        overflowX: "auto",
        minHeight: 88,
      }}
    >
      {Array.from({ length: count }, (_, i) => (
        <StatCellSkeleton key={i} showDivider={i < count - 1} />
      ))}
    </Box>
  );
}

export function PlatformOverviewSkeleton() {
  return (
    <Box sx={{ p: "24px", display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <StatsRowSkeleton count={8} />
        <StatsRowSkeleton count={3} />
      </Box>

      <Box sx={{ ...cardSx, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: "20px",
            py: "16px",
            borderBottom: "1px solid #E5E9EC",
          }}
        >
          <Skeleton variant="text" width={160} height={22} sx={{ borderRadius: "4px" }} />
          <Skeleton variant="text" width={56} height={18} sx={{ borderRadius: "4px" }} />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: recentGrid,
            gap: 2,
            px: "20px",
            height: 40,
            alignItems: "center",
            borderBottom: "1px solid #E5E9EC",
            bgcolor: "#F9FAFA",
          }}
        >
          <Skeleton variant="text" width={72} height={16} sx={{ borderRadius: "4px" }} />
          <Skeleton variant="text" width={48} height={16} sx={{ borderRadius: "4px" }} />
          <Skeleton variant="text" width={72} height={16} sx={{ borderRadius: "4px" }} />
        </Box>

        {Array.from({ length: 5 }, (_, i) => (
          <Box
            key={i}
            sx={{
              display: "grid",
              gridTemplateColumns: recentGrid,
              gap: 2,
              px: "20px",
              minHeight: 56,
              alignItems: "center",
              borderBottom: i === 4 ? "none" : "1px solid #EEF2F6",
            }}
          >
            <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
              <Skeleton variant="text" width="70%" height={18} sx={{ borderRadius: "4px" }} />
              <Skeleton variant="text" width="45%" height={14} sx={{ borderRadius: "4px" }} />
            </Box>
            <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: "100px" }} />
            <Skeleton variant="text" width="80%" height={18} sx={{ borderRadius: "4px" }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function PlatformBusinessDetailSkeleton() {
  return (
    <Box sx={{ p: "24px", display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: "12px" }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Skeleton variant="text" width={220} height={28} sx={{ borderRadius: "4px" }} />
            <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: "100px" }} />
          </Box>
          <Skeleton variant="text" width={200} height={18} sx={{ mt: 0.5, borderRadius: "4px" }} />
        </Box>
        <Skeleton variant="rounded" width={112} height={40} sx={{ borderRadius: "8px" }} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 2 }}>
        {Array.from({ length: 4 }, (_, i) => (
          <Box key={i} sx={{ ...cardSx, p: 2, minWidth: 0 }}>
            <Skeleton variant="text" width="40%" height={16} sx={{ borderRadius: "4px" }} />
            <Skeleton variant="text" width="55%" height={26} sx={{ mt: 0.5, borderRadius: "4px" }} />
            <Skeleton variant="text" width="35%" height={14} sx={{ mt: 0.5, borderRadius: "4px" }} />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
        {[0, 1].map((card) => (
          <Box key={card} sx={{ ...cardSx, p: "20px" }}>
            <Skeleton variant="text" width={160} height={22} sx={{ mb: 2, borderRadius: "4px" }} />
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2.5 }}>
              {Array.from({ length: card === 0 ? 8 : 5 }, (_, i) => (
                <Box key={i} sx={{ minWidth: 0 }}>
                  <Skeleton variant="text" width="45%" height={14} sx={{ mb: 0.75, borderRadius: "4px" }} />
                  <Skeleton variant="text" width="75%" height={18} sx={{ borderRadius: "4px" }} />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
